import React, { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { useOverlayA11y } from '../../hooks/useOverlayA11y'
import { CredentialingBadge } from '../../components/ui'
import { ProviderCredentialingPanel } from '../../components/ProviderCredentialingPanel'
import type { Organization, Provider } from '../../lib/types'

export default function AdminCredentialingQueue() {
  const [providers, setProviders] = useState<Provider[]>([])
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [reviewing, setReviewing] = useState<Provider | null>(null)

  async function load() {
    setLoading(true)
    const qs = statusFilter ? `?status=${statusFilter}` : ''
    const [q, o] = await Promise.all([
      api(`/admin/credentialing-queue${qs}`),
      api('/admin/organizations'),
    ])
    setProviders(q)
    setOrgs(o)
    setLoading(false)
  }

  useEffect(() => { load() }, [statusFilter])

  function orgName(id: string) {
    return orgs.find((o) => o.id === id)?.name || `org ${id.slice(0, 8)}`
  }

  return (
    <div>
      <div className="page-head">
        <h1>Credentialing review queue</h1>
        <div className="ph-sub">Cross-organization providers awaiting verification.</div>
      </div>

      <div className="field" style={{ maxWidth: 240, marginBottom: 16 }}>
        <label>Filter by status</label>
        <select className="inp" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Submitted + in review (default)</option>
          <option value="unverified">Unverified</option>
          <option value="submitted">Submitted</option>
          <option value="in_review">In review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="page-loading">Loading…</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Provider</th><th>Organization</th><th>NPI</th><th>License</th><th>Status</th><th>Updated</th><th></th></tr></thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{orgName(p.organization_id)}</td>
                  <td>{p.npi_number || '—'}</td>
                  <td>{p.license_number ? `${p.license_number} (${p.license_state || '—'})` : '—'}</td>
                  <td><CredentialingBadge status={p.credentialing_status} /></td>
                  <td>{new Date(p.updated_at).toLocaleDateString()}</td>
                  <td><button className="btn small outline" onClick={() => setReviewing(p)}>Review</button></td>
                </tr>
              ))}
              {providers.length === 0 && <tr><td colSpan={7} className="muted">Nothing in the queue.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {reviewing && (
        <CredentialingReviewModal
          provider={reviewing}
          orgName={orgName(reviewing.organization_id)}
          onClose={() => setReviewing(null)}
          onReviewed={load}
        />
      )}
    </div>
  )
}

function CredentialingReviewModal({ provider, orgName, onClose, onReviewed }: {
  provider: Provider
  orgName: string
  onClose: () => void
  onReviewed: () => void
}) {
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const modalRef = useOverlayA11y(onClose)

  async function decide(decision: 'approved' | 'rejected' | 'in_review' | 'action_required') {
    setBusy(true)
    try {
      await api(`/providers/${provider.id}/credentialing/review`, {
        method: 'POST',
        body: JSON.stringify({ decision, notes: notes || null }),
      })
      onReviewed()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" ref={modalRef} role="dialog" aria-modal="true" aria-label={`Credentialing review for ${provider.name}`} tabIndex={-1}>
        <h2>{provider.name}</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -8 }}>{orgName}</p>

        <ProviderCredentialingPanel provider={provider} editable={false} onUpdated={onReviewed} />

        <div className="field" style={{ marginTop: 14 }}>
          <label>Reviewer notes</label>
          <textarea className="inp" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes for this decision — required reading for Action Required, since it's the only place the provider sees what to fix" />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button className="btn small" style={{ background: 'var(--green)' }} disabled={busy} onClick={() => decide('approved')}>Approve</button>
          <button className="btn small" style={{ background: 'var(--red)' }} disabled={busy} onClick={() => decide('rejected')}>Reject</button>
          <button className="btn small outline" style={{ borderColor: 'var(--orange)', color: 'var(--orange)' }} disabled={busy} onClick={() => decide('action_required')}>Request more info</button>
          <button className="btn small outline" disabled={busy} onClick={() => decide('in_review')}>Mark in review</button>
          <button className="btn small outline" disabled={busy} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
